import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ANALYTICS_EVENTS,
  type ProjectSwitchedProperties,
  type ProjectSwitcherOpenedProperties,
} from "analytics";
import { PATH_WORKSPACE } from "config";
import { buildProjectHref, resolveProjectSwitchSegment } from "config/navigation";
import { useAnalytics } from "hooks/useAnalytics";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import { resolveWorkspacePath } from "utils/routing/workspaceRouteUtils";
import { useAppSidebarLayout } from "../appSidebarLayout";
import { useTrackNavClick } from "../useTrackNavClick";
import { DraftSwitcher, ProjectSwitcher, type DraftListProps } from "./ProjectSwitcher";
import { getProjectLicenseLabel } from "./switcherProjects";
import {
  useCurrentDraftId,
  useProjectDrafts,
  useSwitchProjectDraft,
  useSwitcherProjects,
} from "./useProjectSwitcherData";

interface ProjectSwitcherContainerProps {
  workspaceId: string;
  project: ProjectState;
  canCreateProject: boolean;
  onNewProject: () => void;
}

type OpenSwitcher = "project" | "draft" | null;

/**
 * The project and draft triggers at the start of the header breadcrumb.
 * Below md there is one trigger, the project, and its popover lists the
 * drafts too.
 */
export const ProjectSwitcherContainer = ({
  workspaceId,
  project,
  canCreateProject,
  onNewProject,
}: ProjectSwitcherContainerProps) => {
  const [openSwitcher, setOpenSwitcher] = useState<OpenSwitcher>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { trackEvent } = useAnalytics();
  const trackNavClick = useTrackNavClick();
  const { collapsed, device } = useAppSidebarLayout();
  const projectId = String(project.projectId);
  const combined = device === "mobile";

  const { drafts, isLoading: draftsLoading } = useProjectDrafts(project);
  const currentDraftId = useCurrentDraftId(project);
  const { canSwitchDraft, switchDraft, isSwitching } = useSwitchProjectDraft(project);
  const { projects } = useSwitcherProjects(workspaceId, openSwitcher === "project");

  const otherProjects = useMemo(
    () => projects.filter((candidate) => candidate.projectId !== projectId),
    [projectId, projects]
  );
  const currentDraftLabel =
    drafts.find((draft) => draft.documentId === currentDraftId)?.label ?? null;

  const close = () => setOpenSwitcher(null);

  const handleProjectOpenChange = (nextOpen: boolean) => {
    setOpenSwitcher(nextOpen ? "project" : null);
    if (!nextOpen) return;
    trackNavClick({ area: "header", item: "project-switcher" });
    trackEvent(ANALYTICS_EVENTS.PROJECT_SWITCHER_OPENED, {
      project_id: projectId,
      workspace_id: workspaceId,
      collapsed,
      device,
    } satisfies ProjectSwitcherOpenedProperties);
  };

  const handleDraftOpenChange = (nextOpen: boolean) => {
    setOpenSwitcher(nextOpen ? "draft" : null);
    if (nextOpen) trackNavClick({ area: "header", item: "draft-switcher" });
  };

  const draftList: DraftListProps = {
    projectName: project.name,
    drafts,
    currentDraftId,
    draftsLoading,
    canSwitchDraft,
    draftSwitchPending: isSwitching,
    onSelectDraft: (draft) => {
      close();
      void switchDraft(draft);
    },
  };

  return (
    <div className="flex min-w-0 items-center gap-1">
      <ProjectSwitcher
        projectName={project.name}
        projects={otherProjects}
        canCreateProject={canCreateProject}
        open={openSwitcher === "project"}
        onOpenChange={handleProjectOpenChange}
        draftList={combined ? draftList : undefined}
        licenseLabel={getProjectLicenseLabel(project)}
        licenseInMenu={combined}
        onSelectProject={(target, via) => {
          close();
          trackEvent(ANALYTICS_EVENTS.PROJECT_SWITCHED, {
            from_project_id: projectId,
            to_project_id: target.projectId,
            workspace_id: workspaceId,
            via,
          } satisfies ProjectSwitchedProperties);
          navigate(
            buildProjectHref(workspaceId, target.projectId, resolveProjectSwitchSegment(pathname))
          );
        }}
        onAllProjects={() => {
          close();
          navigate(resolveWorkspacePath(PATH_WORKSPACE.workspaceProjects, workspaceId));
        }}
        onNewProject={() => {
          close();
          onNewProject();
        }}
      />
      {combined ? null : (
        <DraftSwitcher
          {...draftList}
          currentDraftLabel={currentDraftLabel}
          open={openSwitcher === "draft"}
          onOpenChange={handleDraftOpenChange}
        />
      )}
    </div>
  );
};
